/**
 * Venda concluída — o que a tela mostra depois do 201.
 *
 * TUDO aqui vem da resposta do servidor: o código `V0001`, o total, o troco e
 * as parcelas geradas. Nenhum número desta tela é recalculado no front, e é
 * deliberado: se o total do carrinho divergir do total gravado, o que o
 * operador precisa ler é o que ficou GRAVADO — é esse valor que vai para a
 * conciliação do caixa e para o carnê do cliente.
 *
 * O troco só aparece quando existe. Um "Troco R$ 0,00" grande na tela é a
 * forma mais fácil de alguém abrir a gaveta sem precisar.
 */
import { Icone } from '../../componentes/Icone'
import { formatarMoeda } from '../../plataforma/dinheiro'
import type { Venda } from '../../plataforma/vendas'

export function VendaConcluida({ venda, aoNovaVenda }: { venda: Venda; aoNovaVenda: () => void }) {
  const pecas = venda.itens.reduce((soma, item) => soma + item.quantidade, 0)

  return (
    <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto bg-fundo p-6">
      <div className="w-full max-w-[520px] rounded-card border border-borda bg-superficie p-6 shadow-[var(--shadow-cartao)]">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ok-suave text-ok-texto">
            <Icone nome="check_circle" tamanho={24} />
          </span>
          <div>
            <h2 className="font-titulo text-[19px] font-bold text-titulo">Venda concluída</h2>
            <p className="tabular mt-0.5 text-[13px] text-texto-suave">
              {venda.codigo}
              {venda.cliente ? ` · ${venda.cliente.nome}` : ' · sem cliente identificado'}
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-sistema bg-superficie-suave p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-texto-suave">Total</span>
            <span className="tabular text-[28px] font-bold text-tinta">
              {formatarMoeda(venda.total_centavos)}
            </span>
          </div>

          {venda.desconto_centavos > 0 && (
            <div className="mt-1.5 flex justify-between text-[12.5px] text-atencao-texto">
              <span>Desconto</span>
              <span className="tabular font-semibold">
                − {formatarMoeda(venda.desconto_centavos)}
              </span>
            </div>
          )}

          {venda.troco_centavos > 0 && (
            <div className="mt-3 flex items-baseline justify-between border-t border-borda pt-3">
              <span className="text-[13.5px] font-semibold text-tinta">Troco</span>
              <span className="tabular text-[22px] font-bold text-ok">
                {formatarMoeda(venda.troco_centavos)}
              </span>
            </div>
          )}
        </div>

        <dl className="mt-4 text-[13px]">
          <Linha rotulo="Peças" valor={`${pecas}`} />
          {venda.pagamentos.map((pagamento, indice) => (
            <Linha
              key={`${pagamento.forma}-${indice}`}
              rotulo={
                pagamento.parcelas > 1
                  ? `${pagamento.forma_rotulo} em ${pagamento.parcelas}×`
                  : pagamento.forma_rotulo
              }
              valor={formatarMoeda(pagamento.valor_centavos)}
            />
          ))}
          {venda.vendedor && <Linha rotulo="Vendedor" valor={venda.vendedor} />}
        </dl>

        {venda.parcelas_geradas > 0 && (
          <div className="mt-4 rounded-sistema border border-borda p-3.5">
            <p className="text-[13px] font-semibold text-tinta">
              {venda.parcelas_geradas === 1
                ? '1 parcela gerada no contas a receber'
                : `${venda.parcelas_geradas} parcelas geradas no contas a receber`}
            </p>
            <ul className="mt-2">
              {venda.parcelas.map((parcela) => (
                <li
                  key={parcela.codigo}
                  className="tabular flex justify-between border-t border-divisoria py-1.5 text-[12.5px] text-texto-suave first:border-t-0"
                >
                  <span>
                    {parcela.codigo} · {parcela.numero_parcela}/{parcela.total_parcelas} · vence{' '}
                    {formatarData(parcela.vencimento)}
                  </span>
                  <span className="font-semibold text-tinta">
                    {formatarMoeda(parcela.valor_centavos)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          autoFocus
          onClick={aoNovaVenda}
          className="mt-5 flex h-[46px] w-full items-center justify-center gap-2 rounded-sistema bg-marca text-sm font-bold text-white hover:bg-marca-forte"
        >
          <Icone nome="add" tamanho={19} />
          Nova venda
        </button>
      </div>
    </div>
  )
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3 border-t border-divisoria py-2 first:border-t-0">
      <dt className="text-texto-suave">{rotulo}</dt>
      <dd className="tabular font-semibold text-tinta">{valor}</dd>
    </div>
  )
}

/** `2026-10-05` → `05/10/2026`, sem passar por `Date` (que desloca no fuso). */
function formatarData(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-')
  if (!ano || !mes || !dia) return iso
  return `${dia}/${mes}/${ano}`
}
